import { apiSlice } from "../../api/apiSlice";

export const EMPLOYEE_ACTIVITY_PAGE_SIZE = 50;
export const EMPLOYEE_SEQUENTIAL_PROGRESS_PAGE_SIZE = 50;

const mergeEmployeeActivityPages = (currentCache, newItems, { arg }) => {
  if (arg.exportAll || !arg.page || arg.page <= 1) {
    return newItems;
  }

  // Idempotent: keep rows for pages before `arg.page`, then replace that page's
  // slice. Safe when invalidateTags refetches page > 1 instead of concatenating.
  const pageSize = arg.limit ?? EMPLOYEE_ACTIVITY_PAGE_SIZE;
  const keepCount = (arg.page - 1) * pageSize;
  const previousRows = currentCache?.data ?? [];

  return {
    ...newItems,
    data: [...previousRows.slice(0, keepCount), ...(newItems.data ?? [])],
    pagination: newItems.pagination,
  };
};

const employeeActivitySerializeKey = ({
  sortBy,
  type,
  query,
  startDate,
  endDate,
  companyId,
}) =>
  `${sortBy ?? ""}-${type ?? ""}-${query ?? ""}-${startDate ?? ""}-${endDate ?? ""}-${companyId ?? ""}`;

const mergeEmployeeSequentialProgressPages = (currentCache, newItems, { arg }) => {
  if (arg.exportAll || !arg.page || arg.page <= 1) {
    return newItems;
  }

  const pageSize = arg.limit ?? EMPLOYEE_SEQUENTIAL_PROGRESS_PAGE_SIZE;
  const keepCount = (arg.page - 1) * pageSize;
  const previousRows = currentCache?.data ?? [];

  return {
    ...newItems,
    data: [...previousRows.slice(0, keepCount), ...(newItems.data ?? [])],
    pagination: newItems.pagination,
  };
};

const employeeSequentialProgressSerializeKey = ({ sortBy, type, query }) =>
  `${sortBy ?? ""}-${type ?? ""}-${query ?? ""}`;

export const reportsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCompanyBookSummary: builder.query({
      query: ({ sortBy, type, query, startDate, endDate }) => ({
        url: `/api/reports/super-admin/company-book-summary?sort_by=${sortBy}&type=${type}&query=${query}${
          startDate ? `&startDate=${startDate}` : ""
        }${endDate ? `&endDate=${endDate}` : ""}`,
        method: "GET",
      }),
    }),
    getCompanyUserSummary: builder.query({
      query: ({ sortBy, type, query, startDate, endDate }) => ({
        url: `/api/reports/super-admin/company-user-summary?sort_by=${sortBy}&type=${type}&query=${query}${
          startDate ? `&startDate=${startDate}` : ""
        }${endDate ? `&endDate=${endDate}` : ""}`,
        method: "GET",
      }),
    }),
    getCompanyUserStatistics: builder.query({
      query: ({ sortBy, type, query, startDate, endDate }) => ({
        url: `/api/reports/super-admin/organization-user-statistics?sort_by=${sortBy}&type=${type}&query=${query}${
          startDate ? `&startDate=${startDate}` : ""
        }${endDate ? `&endDate=${endDate}` : ""}`,
        method: "GET",
      }),
    }),
    getCompanyWiseBreakdown: builder.query({
      query: ({ sortBy, type, query, startDate, endDate }) => ({
        url: `/api/reports/super-admin/company-wise-breakdown?sort_by=${sortBy}&type=${type}&query=${query}${
          startDate ? `&startDate=${startDate}` : ""
        }${endDate ? `&endDate=${endDate}` : ""}`,
        method: "GET",
      }),
    }),
    getSuperAdminOrgBookSummary: builder.query({
      query: ({ query }) => {
        const params = new URLSearchParams();
        if (query) params.set("query", query);
        const qs = params.toString();
        return {
          url: `/api/reports/super-admin/org-book-summary${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
    }),
    getSuperAdminEmployeeSequentialProgress: builder.query({
      query: ({ sortBy, type, query, page, limit, exportAll }) => {
        const params = new URLSearchParams();
        if (sortBy) params.set("sort_by", sortBy);
        if (type) params.set("type", type);
        if (query) params.set("query", query);
        if (exportAll) {
          params.set("export", "true");
        } else {
          params.set("page", String(page ?? 1));
          params.set(
            "limit",
            String(limit ?? EMPLOYEE_SEQUENTIAL_PROGRESS_PAGE_SIZE)
          );
        }
        return {
          url: `/api/reports/super-admin/employee-sequential-progress?${params.toString()}`,
          method: "GET",
        };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        queryArgs.exportAll
          ? `export-${employeeSequentialProgressSerializeKey(queryArgs)}`
          : employeeSequentialProgressSerializeKey(queryArgs),
      merge: mergeEmployeeSequentialProgressPages,
      forceRefetch: ({ currentArg, previousArg }) =>
        !currentArg.exportAll && currentArg.page !== previousArg?.page,
    }),
    getSuperAdminEmployeeActivity: builder.query({
      query: ({
        sortBy,
        type,
        query,
        startDate,
        endDate,
        page,
        limit,
        exportAll,
      }) => {
        const params = new URLSearchParams();
        if (sortBy) params.set("sort_by", sortBy);
        if (type) params.set("type", type);
        if (query) params.set("query", query);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        if (exportAll) {
          params.set("export", "true");
        } else {
          params.set("page", String(page ?? 1));
          params.set("limit", String(limit ?? EMPLOYEE_ACTIVITY_PAGE_SIZE));
        }
        return {
          url: `/api/reports/super-admin/employee-activity?${params.toString()}`,
          method: "GET",
        };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        queryArgs.exportAll
          ? `export-${employeeActivitySerializeKey(queryArgs)}`
          : employeeActivitySerializeKey(queryArgs),
      merge: mergeEmployeeActivityPages,
      forceRefetch: ({ currentArg, previousArg }) =>
        !currentArg.exportAll && currentArg.page !== previousArg?.page,
    }),
    getAdminEmployeeActivity: builder.query({
      query: ({
        sortBy,
        type,
        query,
        startDate,
        endDate,
        page,
        limit,
        exportAll,
        companyId,
      }) => {
        const params = new URLSearchParams();
        if (sortBy) params.set("sort_by", sortBy);
        if (type) params.set("type", type);
        if (query) params.set("query", query);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        if (companyId) params.set("companyId", companyId);
        if (exportAll) {
          params.set("export", "true");
        } else {
          params.set("page", String(page ?? 1));
          params.set("limit", String(limit ?? EMPLOYEE_ACTIVITY_PAGE_SIZE));
        }
        return {
          url: `/api/reports/admin/employee-activity?${params.toString()}`,
          method: "GET",
        };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        `admin-${
          queryArgs.exportAll
            ? `export-${employeeActivitySerializeKey(queryArgs)}`
            : employeeActivitySerializeKey(queryArgs)
        }`,
      merge: mergeEmployeeActivityPages,
      forceRefetch: ({ currentArg, previousArg }) =>
        !currentArg.exportAll &&
        (currentArg.page !== previousArg?.page ||
          currentArg.companyId !== previousArg?.companyId),
      providesTags: ["AdminReports"],
    }),
    getAdminCompanyWiseBreakdown: builder.query({
      query: ({ sortBy, type, query, startDate, endDate, companyId }) => {
        const params = new URLSearchParams();
        if (sortBy) params.set("sort_by", sortBy);
        if (type) params.set("type", type);
        if (query) params.set("query", query);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        if (companyId) params.set("companyId", companyId);
        return {
          url: `/api/reports/admin/company-wise-breakdown?${params.toString()}`,
          method: "GET",
        };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        `admin-cwb-${queryArgs.sortBy ?? ""}-${queryArgs.type ?? ""}-${queryArgs.query ?? ""}-${queryArgs.startDate ?? ""}-${queryArgs.endDate ?? ""}-${queryArgs.companyId ?? ""}`,
      providesTags: ["AdminReports"],
    }),
    getAdminOrgBookSummary: builder.query({
      query: ({ query, companyId }) => {
        const params = new URLSearchParams();
        if (query) params.set("query", query);
        if (companyId) params.set("companyId", companyId);
        const qs = params.toString();
        return {
          url: `/api/reports/admin/org-book-summary${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        `admin-obs-${queryArgs.query ?? ""}-${queryArgs.companyId ?? ""}`,
      providesTags: ["AdminReports"],
    }),
  }),
});

export const {
  useGetCompanyUserStatisticsQuery,
  useGetCompanyWiseBreakdownQuery,
  useGetCompanyUserSummaryQuery,
  useGetCompanyBookSummaryQuery,
  useGetSuperAdminOrgBookSummaryQuery,
  useGetSuperAdminEmployeeSequentialProgressQuery,
  useLazyGetSuperAdminEmployeeSequentialProgressQuery,
  useGetSuperAdminEmployeeActivityQuery,
  useLazyGetSuperAdminEmployeeActivityQuery,
  useGetAdminEmployeeActivityQuery,
  useLazyGetAdminEmployeeActivityQuery,
  useGetAdminCompanyWiseBreakdownQuery,
  useGetAdminOrgBookSummaryQuery,
} = reportsApiSlice;
